import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import {
   AlertConfigurationResponse,
   requestAlertConfiguration,
   requestAlertConfigurations,
   requestUpdateBulkAlertConfigurations,
   requestUpdateAlertConfiguration,
   requestCreateAlertConfiguration,
   requestDeleteAlertConfiguration,
} from "@/api/AlertConfigurationsService"
import { AlertConfigurationModule } from "@/pages/Settings/Alerts/types"
import {
   AlertConfiguration,
   AlertConfigurationForm,
} from "@/types/alertConfigurations"
import {
   formatAlertConfiguration,
   formatAlertConfigurations,
} from "@/utils/alertConfiguration"

interface AlertConfigurationsState {
   loading: boolean
   error: number | null
   alertConfigurations: AlertConfiguration[]
   loadingAlertConfiguration: boolean
   errorAlertConfiguration: number | null
   alertConfiguration: AlertConfiguration
   updatingModule: string
   loadingAlertConfigurationModule: boolean
   errorAlertConfigurationModule: number | null
}

export const initialState: AlertConfigurationsState = {
   loading: false,
   error: null,
   alertConfigurations: [],
   loadingAlertConfiguration: false,
   errorAlertConfiguration: null,
   alertConfiguration: {} as AlertConfiguration,
   updatingModule: "",
   loadingAlertConfigurationModule: false,
   errorAlertConfigurationModule: null,
}

export const fetchAlertConfigurations = createAsyncThunk(
   "alertConfigurations/fetchAlertConfigurations",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<AlertConfigurationResponse[]> =
            await requestAlertConfigurations()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateBulkAlertConfigurations = createAsyncThunk(
   "alertConfigurations/updateBulkAlertConfigurations",
   async (
      {
         alertConfigurations,
      }: {
         alertConfigurations: Partial<AlertConfigurationForm>[]
      },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<AlertConfigurationResponse[]> =
            await requestUpdateBulkAlertConfigurations(alertConfigurations)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const fetchAlertConfiguration = createAsyncThunk(
   "alertConfigurations/fetchAlertConfiguration",
   async ({ alertId }: { alertId: string }, thunkAPI) => {
      try {
         const response: ApiResponse<AlertConfigurationResponse> =
            await requestAlertConfiguration(alertId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const createAlertConfiguration = createAsyncThunk(
   "alertConfigurations/createAlertConfiguration",
   async (
      {
         alertConfiguration,
      }: { alertConfiguration: Partial<AlertConfigurationForm> },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<AlertConfigurationResponse> =
            await requestCreateAlertConfiguration(alertConfiguration)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateAlertConfiguration = createAsyncThunk(
   "alertConfigurations/updateAlertConfiguration",
   async (
      {
         alertId,
         alertData,
         updatingModule,
      }: {
         alertId: string
         alertData: Partial<AlertConfigurationForm>
         updatingModule: AlertConfigurationModule
      },
      thunkAPI
   ) => {
      thunkAPI.dispatch(setUpdatingModule(updatingModule))
      try {
         const response: ApiResponse<AlertConfigurationResponse> =
            await requestUpdateAlertConfiguration(alertId, alertData)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const deleteAlertConfiguration = createAsyncThunk(
   "alertConfigurations/deleteAlertConfiguration",
   async ({ alertId }: { alertId: string }, thunkAPI) => {
      try {
         const response: ApiResponse<null> =
            await requestDeleteAlertConfiguration(alertId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const alertConfigurationsSlice = createSlice({
   name: "alert-configurations",
   initialState,
   reducers: {
      resetAlertConfiguration: () => initialState,
      setUpdatingModule: (state, action: PayloadAction<string>) => {
         state.updatingModule = action.payload
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchAlertConfigurations.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchAlertConfigurations.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<AlertConfigurationResponse[]>>
            ) => {
               state.loading = false
               state.alertConfigurations = formatAlertConfigurations(
                  action.payload.data
               ) as unknown as AlertConfiguration[]
            }
         )
         .addCase(fetchAlertConfigurations.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(updateBulkAlertConfigurations.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(updateBulkAlertConfigurations.fulfilled, (state, action) => {
            state.loading = false
            state.alertConfigurations = action.payload.data
               .map((updatedItem) => {
                  const existing = state.alertConfigurations.find(
                     (item) => item.id === updatedItem.id
                  )
                  if (!existing) {
                     return
                  }
                  return {
                     ...existing,
                     ...updatedItem,
                  }
               })
               .filter((item) => item !== undefined)
         })
         .addCase(updateBulkAlertConfigurations.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(fetchAlertConfiguration.pending, (state) => {
            state.loadingAlertConfiguration = true
            state.errorAlertConfiguration = null
         })
         .addCase(fetchAlertConfiguration.fulfilled, (state, action) => {
            state.loadingAlertConfiguration = false
            Object.assign(
               state.alertConfiguration,
               formatAlertConfiguration(action.payload.data)
            )
         })
         .addCase(fetchAlertConfiguration.rejected, (state, action) => {
            state.loadingAlertConfiguration = false
            state.errorAlertConfiguration = action.payload as number
         })
         .addCase(createAlertConfiguration.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(createAlertConfiguration.fulfilled, (state, action) => {
            state.loading = false
            Object.assign(state.alertConfigurations, [
               ...state.alertConfigurations,
               formatAlertConfiguration(action.payload.data),
            ])
         })
         .addCase(createAlertConfiguration.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(updateAlertConfiguration.pending, (state) => {
            state.loadingAlertConfigurationModule = true
            state.errorAlertConfigurationModule = null
         })
         .addCase(
            updateAlertConfiguration.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<AlertConfigurationResponse>>
            ) => {
               state.loadingAlertConfigurationModule = false
               state.updatingModule = ""
               Object.assign(
                  state.alertConfiguration,
                  formatAlertConfiguration(action.payload.data)
               )
            }
         )
         .addCase(updateAlertConfiguration.rejected, (state, action) => {
            state.loadingAlertConfiguration = false
            state.errorAlertConfiguration = action.payload as number
            state.updatingModule = ""
         })
         .addCase(deleteAlertConfiguration.pending, (state) => {
            state.loadingAlertConfiguration = true
            state.errorAlertConfiguration = null
         })
         .addCase(deleteAlertConfiguration.fulfilled, (state) => {
            state.loadingAlertConfiguration = false
         })
         .addCase(deleteAlertConfiguration.rejected, (state, action) => {
            state.loadingAlertConfiguration = false
            state.errorAlertConfiguration = action.payload as number
         })
   },
})

export const { resetAlertConfiguration, setUpdatingModule } =
   alertConfigurationsSlice.actions
export default alertConfigurationsSlice.reducer
